import { useRef, useState, type CSSProperties, type ChangeEvent, type MouseEvent } from 'react';
import { Link } from 'react-router';
import { usePageMeta } from '@/shared/pageMeta';
import bookImage from './assets/scenes/landing-book.webp';
import ideaImage from './assets/scenes/landing-idea.webp';
import styles from './LandingPage.module.css';

// A chain: every line begins with the noun the previous one ended on — идея → мир → герои → выбор
// → история. That is what makes dragging the slider read as one transformation instead of a list
// of stages, and it is why editing a single line here is never local: change what a line ends on
// and the next one has to follow.
//
// The link is deliberately dropped on the LAST step. "История становится книгой" would describe a
// process where the sequence needs an arrival, and this is the moment the CTA appears. Five
// repetitions then a release — a cadence, not an oversight. A break anywhere EARLIER reads as a
// mistake, which is what ruled out ending a middle line on a noun the next one does not pick up.
//
// "Рукопись" was dropped when "выбор" earned its place: the arc идея → … → книга is complete
// without it, and a seventh state would only make the phrases flicker past during a drag.
//
// No trailing periods: these are captions under a headline that already ends in one.
const TRANSFORMATION_STATES = [
  { until: 0, label: 'Всё начинается с идеи', valueText: 'идея' },
  { until: 24, label: 'Идея рождает мир', valueText: 'рождается мир' },
  { until: 49, label: 'Мир обретает героев', valueText: 'мир и герои' },
  { until: 74, label: 'Герои делают выбор', valueText: 'герои и выбор' },
  { until: 99, label: 'Выбор складывается в историю', valueText: 'рождается история' },
  { until: 100, label: 'Книга готова', valueText: 'готовая книга' },
] as const;

type LandingStyle = CSSProperties & {
  '--progress': string;
  '--reveal': number;
  '--light-x': string;
};

function smoothStep(value: number) {
  const progress = Math.min(1, Math.max(0, value));
  return progress * progress * (3 - 2 * progress);
}

function getTransformationState(value: number) {
  return (
    TRANSFORMATION_STATES.find((state) => value <= state.until) ??
    TRANSFORMATION_STATES[TRANSFORMATION_STATES.length - 1]
  );
}

export function LandingPage() {
  // Landing keeps the index.html defaults; the explicit call restores them after
  // client-side navigation back from /login, /privacy, etc.
  usePageMeta();
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const actionRef = useRef<HTMLAnchorElement>(null);
  const keyboardInteractionRef = useRef(false);
  const state = getTransformationState(progress);
  const isComplete = progress === 100;
  const normalizedProgress = progress / 100;
  const landingStyle: LandingStyle = {
    '--progress': `${progress}%`,
    '--reveal': smoothStep(normalizedProgress),
    '--light-x': `${43 + normalizedProgress * 43}%`,
  };

  const handleProgressChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextProgress = Number(event.currentTarget.value);
    setProgress(nextProgress);

    if (nextProgress === 100 && keyboardInteractionRef.current) {
      keyboardInteractionRef.current = false;
      window.requestAnimationFrame(() => actionRef.current?.focus());
    }
  };

  const handleActionClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isComplete) event.preventDefault();
  };

  return (
    <div
      className={styles.landing}
      data-testid="landing-hero"
      data-complete={isComplete}
      data-dragging={isDragging}
      style={landingStyle}
    >
      <a className={styles.skipLink} href="#landing-content">
        Перейти к содержанию
      </a>

      <header className={styles.siteHeader}>
        <span className={styles.brand}>PairPen</span>
      </header>

      <main className={styles.hero} id="landing-content">
        <div className={styles.visualStage} aria-hidden="true">
          <img
            className={`${styles.sceneImage} ${styles.initialImage}`}
            src={ideaImage}
            width={1586}
            height={992}
            alt=""
            data-scene-image="initial"
            loading="eager"
            fetchPriority="high"
            decoding="sync"
          />
          <img
            className={`${styles.sceneImage} ${styles.finalImage}`}
            src={bookImage}
            width={1586}
            height={992}
            alt=""
            data-scene-image="final"
            loading="eager"
            decoding="async"
          />
          <div className={styles.revealLight} />
          <div className={styles.leftShade} />
          <div className={styles.vignette} />
          <div className={styles.grain} />
        </div>

        <section className={styles.heroCopy} aria-labelledby="landing-title">
          <p className={styles.journeyLabel}>
            {/* Was "От идеи до книги" — the third of six restatements of the same arc on one
                screen, and the weakest. This slot now does the job nothing on the page did: say
                what PairPen IS. A visitor saw an image, a promise and a slider, and nowhere the
                word "инструмент". */}
            <span aria-hidden="true" /> Студия написания книг с ИИ
          </p>
          <h1 id="landing-title" className={styles.title}>
            <span>Пока — только идея.</span>
            <span>Здесь она станет книгой.</span>
          </h1>
          {/* The slider's own commentary, promoted from a 0.7rem line under the control to the
              subtitle slot. It used to sit beside a static summary that said the same thing in
              flat prose — this makes the sentence the thing that moves. Keeps the
              `landing-control-state` testid, so the smoke test still follows the real copy.
              aria-live lives on the stable <p>; only the inner <span> is keyed and re-mounted, so
              the announcement is not lost to a swapped host element. */}
          <p
            className={styles.stateLine}
            data-testid="landing-control-state"
            aria-live="polite"
            aria-atomic="true"
          >
            <span key={state.label} className={styles.stateLineText}>
              {state.label}
            </span>
          </p>
          <div className={styles.actionSlot}>
            <Link
              ref={actionRef}
              className={styles.primaryAction}
              data-testid="landing-cta"
              to="/signup"
              aria-disabled={!isComplete}
              tabIndex={isComplete ? 0 : -1}
              onClick={handleActionClick}
            >
              Начать книгу
            </Link>
          </div>
        </section>

        <section className={styles.transformationControl} aria-labelledby="slider-title">
          <div className={styles.controlCopy}>
            {/* Was "Проведите идею до книги" — a fourth restatement, and not even the label's job.
                The control is already bracketed by "Идея … Готовая книга"; what was missing is that
                it can be dragged at all. */}
            <label className={styles.controlTitle} id="slider-title" htmlFor="transformation-range">
              Потяните ползунок
            </label>
          </div>

          <div className={styles.rangeShell}>
            <div className={styles.rangeLabels} aria-hidden="true">
              <span>Идея</span>
              <span>Готовая книга</span>
            </div>
            <div className={styles.rangeControl}>
              {/* No aria-label here on purpose. It used to read "Превращение идеи в готовую книгу"
                  — the sixth restatement of the same arc on one screen, and an aria-label OVERRIDES
                  the visible <label>, so the accessible name shared no words with the text on
                  screen (WCAG 2.5.3 Label in Name; it also breaks voice control, where the user
                  speaks what they can read). The <label for> names the control now, and
                  aria-valuetext still carries the state. */}
              <input
                className={styles.rangeInput}
                data-testid="landing-transformation-range"
                id="transformation-range"
                type="range"
                min="0"
                max="100"
                step="1"
                value={progress}
                aria-valuetext={`${progress} процентов — ${state.valueText}`}
                onChange={handleProgressChange}
                onKeyDown={() => {
                  keyboardInteractionRef.current = true;
                }}
                onPointerDown={() => {
                  keyboardInteractionRef.current = false;
                  setIsDragging(true);
                }}
                onPointerUp={() => setIsDragging(false)}
                onPointerCancel={() => setIsDragging(false)}
                onBlur={() => setIsDragging(false)}
              />
              <div className={styles.rangeVisual} aria-hidden="true">
                <span className={styles.rangeTrack} />
                <span className={styles.rangeFill} />
                <span className={styles.rangeHandle}>
                  <span className={styles.rangeHandleLine} />
                  <span className={styles.rangeHandleChevrons}>››</span>
                </span>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* B-PAY / T-P03. The landing used to be a hero and nothing else, so the legal and commercial
          pages existed but had no route in from the site's front door — a ЮKassa moderator would
          have found no prices, no offer and no requisites. Static markup, no new assets: the LCP
          posture that T-S03 bought is untouched. */}
      {/* One row, links and requisites side by side. The full titles («Условия использования»,
          «Политика конфиденциальности») do not fit on a single line beside the legal text even at
          1440, so the labels are short here; the pages carry their full names in their own <h1>.
          The email is dropped from this line for the same reason — it is on /contacts, which is
          one click away. */}
      <footer className={styles.siteFooter}>
        <nav className={styles.footerNav} aria-label="Документы и информация">
          <Link className={styles.footerLink} to="/pricing">
            Тарифы
          </Link>
          <Link className={styles.footerLink} to="/offer">
            Оферта
          </Link>
          <Link className={styles.footerLink} to="/terms">
            Условия
          </Link>
          <Link className={styles.footerLink} to="/privacy">
            Конфиденциальность
          </Link>
          <Link className={styles.footerLink} to="/contacts">
            Контакты
          </Link>
        </nav>
        <p className={styles.footerLegal}>
          © 2026 PairPen · Лавриков Виталий Вячеславович · ИНН 860806437501
        </p>
      </footer>
    </div>
  );
}
